import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmailComposer } from "@/components/shared/email-composer";

interface ReinviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Client's accountant-user id — drives the invite preview so the
   *  accountant sees exactly what will be sent. */
  clientId: string;
  clientName: string;
  clientEmail?: string;
  /** Business name used for the default subject. */
  accountantName?: string;
  loading?: boolean;
  onConfirm: (personalNote: string, subject: string) => void;
}

export function ReinviteDialog({
  open,
  onOpenChange,
  clientId,
  clientName,
  clientEmail,
  accountantName,
  loading = false,
  onConfirm,
}: ReinviteDialogProps) {
  const [resetKey, setResetKey] = useState(0);

  const handleOpenChange = (next: boolean) => {
    if (!next) setResetKey((k) => k + 1);
    onOpenChange(next);
  };

  const defaultSubject = accountantName
    ? `${accountantName} has invited you to Taxr`
    : "You're invited to Taxr";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-5xl lg:max-w-6xl xl:max-w-7xl">
        <DialogHeader>
          <DialogTitle>Re-invite {clientName}</DialogTitle>
          <DialogDescription>
            We'll email a fresh invite. Add a personal note below. It goes
            into the invite email so it's easier for them to respond.
          </DialogDescription>
        </DialogHeader>
        <EmailComposer
          key={resetKey}
          toName={clientName}
          toEmail={clientEmail}
          defaultSubject={defaultSubject}
          initialBody=""
          bodyMode="rich"
          bodyLabel="Personal note"
          bodyPlaceholder="e.g. Hey, following up on the invite I sent last week. Let me know if you need help signing up."
          previewType="invite"
          clientId={clientId}
          sending={loading}
          onSend={({ subject, body }) => onConfirm(body, subject)}
          onCancel={() => onOpenChange(false)}
          sendLabel="Send invite"
        />
      </DialogContent>
    </Dialog>
  );
}
